import type { FlushConfig, FlushDebugState, IFlushManager } from './types';

type ManagedListener = {
  readonly target: 'window' | 'document';
  readonly type: string;
  readonly handler: EventListener;
};

export class FlushManager implements IFlushManager {
  private readonly config: FlushConfig;

  private onFlush: (() => void) | null = null;

  private intervalId: ReturnType<typeof setInterval> | null = null;

  private flushCount = 0;

  private lastFlushAt: number | null = null;

  private readonly listeners: ManagedListener[] = [];

  constructor(config: FlushConfig) {
    this.config = config;
  }

  init(onFlush: () => void): void {
    this.onFlush = onFlush;

    if (this.config.flushIntervalMs > 0) {
      this.intervalId = setInterval(
        () => this.flush(),
        this.config.flushIntervalMs
      );
    }

    if (this.config.prepareUnloadHooks) {
      this.registerUnloadHooks();
    }
  }

  flush(): void {
    if (!this.onFlush) return;

    this.flushCount += 1;
    this.lastFlushAt = Date.now();
    this.onFlush();
  }

  destroy(): void {
    if (this.config.flushOnDestroy) {
      this.flush();
    }

    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }

    this.removeListeners();
    this.onFlush = null;
  }

  getState(): FlushDebugState {
    return {
      flushCount: this.flushCount,
      lastFlushAt: this.lastFlushAt,
      intervalActive: this.intervalId !== null,
    };
  }

  private registerUnloadHooks(): void {
    if (typeof window === 'undefined') return;

    const handler = (): void => this.flush();

    for (const type of ['pagehide', 'beforeunload'] as const) {
      window.addEventListener(type, handler);
      this.listeners.push({ target: 'window', type, handler });
    }

    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', handler);
      this.listeners.push({
        target: 'document',
        type: 'visibilitychange',
        handler,
      });
    }
  }

  private removeListeners(): void {
    for (const entry of this.listeners) {
      if (entry.target === 'window' && typeof window !== 'undefined') {
        window.removeEventListener(entry.type, entry.handler);
      } else if (
        entry.target === 'document' &&
        typeof document !== 'undefined'
      ) {
        document.removeEventListener(entry.type, entry.handler);
      }
    }
    this.listeners.length = 0;
  }
}
